import { Dispatch, SetStateAction } from "react";

type PaginationProps = {
  totalCount: number;
  page_size: number;
  currentPage: number;
  setCurrentPage: Dispatch<SetStateAction<number>>;
  previous: string | null;
  next: string | null;
};

export default function Pagination({
  totalCount,
  page_size,
  currentPage,
  setCurrentPage,
  previous,
  next,
}: PaginationProps) {
  const totalPages = Math.ceil(totalCount / page_size);

  const handlePageClick = (page: number) => {
    if (page > 0 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handleNext = () => {
    if (next) {
      setCurrentPage(currentPage + 1); // Next page
    }
  };

  const handlePrevious = () => {
    if (previous) {
      setCurrentPage(currentPage - 1); // Previous page
    }
  };

  const generatePageNumbers = () => {
    const pages: (number | string)[] = [];
    const maxVisiblePages = 3;

    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, index) => index + 1);
    }

    // Always show first page
    pages.push(1);

    if (currentPage > maxVisiblePages + 1) {
      pages.push("...");
    }

    const start = Math.max(2, currentPage - 1);
    const end = Math.min(totalPages - 1, currentPage + 1);

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    if (currentPage < totalPages - maxVisiblePages) {
      pages.push("...");
    }

    // Always show last page
    pages.push(totalPages);

    return pages;
  };

  return (
    <div className="row mt-3">
      <div className="col-12 d-flex align-items-center justify-content-between flex-wrap">
        <span className="text-muted py-2">
          Showing {(currentPage - 1) * page_size + 1} to {Math.min(totalCount, currentPage * page_size)} of {totalCount}
        </span>

        <nav>
          <ul className="pagination mb-0">
            <li className={`page-item ${currentPage === 1 || !previous ? "disabled" : ""}`}>
              <a className="page-link g2-text-blue" href="#" onClick={handlePrevious}>
                Previous
              </a>
            </li>
            {generatePageNumbers().map((page, index) =>
              typeof page === "number" ? (
                <li key={index} className={`page-item ${page === currentPage ? "active" : ""}`}>
                  <a className="page-link g2-text-blue" href="#" onClick={() => handlePageClick(page)}>
                    {page}
                  </a>
                </li>
              ) : (
                <li key={index} className="page-item disabled">
                  <span className="page-link">...</span>
                </li>
              ),
            )}
            <li className={`page-item ${!next ? "disabled" : ""}`}>
              <a className="page-link g2-text-blue" href="#" onClick={handleNext}>
                Next
              </a>
            </li>
          </ul>
        </nav>
      </div>
    </div>
  );
}
